import { toBoolOrNull, toIntOrNull } from '@utils/parse';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import { DashRegistrationStep } from '@/types/dashboard';
import { defaultHistoryProgress } from '@/types/history/history-progress';
import type {
    IHistoryDiagnosedFormValues,
    IHistoryDiagnosedModel,
    IHistoryDiagnosedSubmitResult,
} from '@/types/history/history-diagnosed';

const HISTORY_DIAGNOSED_ENDPOINT = '/api/register/history-diagnosed';

const defaultHistoryDiagnosedModel: IHistoryDiagnosedModel = {
    ...defaultHistoryProgress,
    currentRegistrationStep: DashRegistrationStep.HistoryDiagnosed,

    useReducedQuestions: false,
    isLiverRegistration: false,
    isFemale: false,
    isMale: false,
    requirePregnancyFields: false,

    medicalStatusStillBeingTreatedValue: 0,
    medicalStatusTreatedInPastValue: 0,
    yesValue: 0,

    aboType: null,
    hasHistoryOfHighBp: null,
    highBpAgents: null,
    hadHighBpDuringPregnancy: null,
    hasHadCabg: null,
    hasHistoryOfBloodClots: null,
    hasHistoryOfCoronaryDisease: null,
    hasHistoryOfHighCholOrTg: null,
    hasStents: null,
    historyChestPainExercise: null,
    historyChestPainOther: null,
    resultsOfClimbingStairs: null,
    hadStressTestWithinYear: null,
    stressTestLocation: '',
    stressTestResults: null,
    hasHadStroke: null,
    howManyStrokes: null,
    hasHadLegBypass: null,
    howManyLegBypasses: null,
    historyLungDisease: null,
    hasCopd: null,
    hasTb: null,
    historyPneumonia: null,
    hasRespiratoryIssues: null,
    respiratoryIssuesDescription: '',
    hasGastroIssues: null,
    gastroIssuesDescription: '',
    historyGallstones: null,
    historyPancreatitis: null,
    historyLiverDisease: null,
    hasHepB: null,
    hasHepC: null,
    hasBleedingProblem: null,
    hasHadUti: null,
    utiEpisodes: null,
    utiRemissionStatus: null,
    historyKidneyDamage: null,
    historyProteinuria: null,
    historyHematuria: null,
    historyNeuroDisease: null,
    historySeizure: null,
    hasLupus: null,
    historyParalysis: null,
    hasArthritis: null,
    historyNeuropathy: null,
    historyDepression: null,
    historyAnxiety: null,
    hasHistoryOfPsychiatric: null,
    psychiatricDescription: '',
    historyFibroidUterus: null,
    historyEndometriosis: null,
    historyPolycysticOvaries: null,
    hasHadMiscarriages: null,
    howManyMiscarriages: null,
    hadLateMiscarriage: null,
    hasHadAbortions: null,
    howManyAbortions: null,
    hasReceivedTransfusion: null,
    howManyTransfusions: null,
    hasHistoryOfObesity: null,
    hasFabrys: null,
    hasSickleCell: null,
    hasSickleCellTrait: null,
    hasSarcoidosis: null,
    hasAutoImmune: null,
    autoImmuneDescription: '',
    hasHadPhysicalExam: null,
    physicalExamYearsAgo: null,
    hasHadMammogram: null,
    mammogramYearsAgo: null,
    hasHadPapSmear: null,
    papSmearYearsAgo: null,
    hasHadPsaTest: null,
    psaTestYearsAgo: null,
    hasHadColonoscopy: null,
    colonoscopyYearsAgo: null,
    hasAnimalBiteExposure: null,
    animalBiteDescription: '',
    hasActiveInfection: null,
    activeInfectionDescription: '',
    additionalComments: '',

    aboTypeOptions: [],
    medicalHistoryStatusOptions: [],
    yesNoUnknownOptions: [],
    climbingStairsOptions: [],
    stressTestResultsOptions: [],
    utiRemissionOptions: [],
};

export const useHistoryDiagnosed = (registrationId: number, editKey: string) => {
    const url = `${HISTORY_DIAGNOSED_ENDPOINT}/${registrationId}/${editKey}`;

    const { model, isLoading, error } = useApiModel(url, defaultHistoryDiagnosedModel);

    const i = toIntOrNull;

    const { submit, isSubmitting, submitError } = useApiSubmit<
        IHistoryDiagnosedFormValues,
        DashRegistrationStep,
        IHistoryDiagnosedSubmitResult
    >(
        url,
        (values) => ({
            aboType: i(values.aboType),
            hasHistoryOfHighBp: i(values.hasHistoryOfHighBp),
            highBpAgents: i(values.highBpAgents),
            hadHighBpDuringPregnancy: i(values.hadHighBpDuringPregnancy),
            hasHadCabg: i(values.hasHadCabg),
            hasHistoryOfBloodClots: i(values.hasHistoryOfBloodClots),
            hasHistoryOfCoronaryDisease: i(values.hasHistoryOfCoronaryDisease),
            hasHistoryOfHighCholOrTg: i(values.hasHistoryOfHighCholOrTg),
            hasStents: i(values.hasStents),
            historyChestPainExercise: i(values.historyChestPainExercise),
            historyChestPainOther: i(values.historyChestPainOther),
            resultsOfClimbingStairs: i(values.resultsOfClimbingStairs),
            hadStressTestWithinYear: i(values.hadStressTestWithinYear),
            stressTestLocation: values.stressTestLocation,
            stressTestResults: i(values.stressTestResults),
            hasHadStroke: i(values.hasHadStroke),
            howManyStrokes: i(values.howManyStrokes),
            hasHadLegBypass: i(values.hasHadLegBypass),
            howManyLegBypasses: i(values.howManyLegBypasses),
            historyLungDisease: i(values.historyLungDisease),
            hasCopd: i(values.hasCopd),
            hasTb: i(values.hasTb),
            historyPneumonia: i(values.historyPneumonia),
            hasRespiratoryIssues: i(values.hasRespiratoryIssues),
            respiratoryIssuesDescription: values.respiratoryIssuesDescription,
            hasGastroIssues: i(values.hasGastroIssues),
            gastroIssuesDescription: values.gastroIssuesDescription,
            historyGallstones: i(values.historyGallstones),
            historyPancreatitis: i(values.historyPancreatitis),
            historyLiverDisease: i(values.historyLiverDisease),
            hasHepB: i(values.hasHepB),
            hasHepC: i(values.hasHepC),
            hasBleedingProblem: i(values.hasBleedingProblem),
            hasHadUti: i(values.hasHadUti),
            utiEpisodes: i(values.utiEpisodes),
            utiRemissionStatus: i(values.utiRemissionStatus),
            historyKidneyDamage: i(values.historyKidneyDamage),
            historyProteinuria: i(values.historyProteinuria),
            historyHematuria: i(values.historyHematuria),
            historyNeuroDisease: i(values.historyNeuroDisease),
            historySeizure: i(values.historySeizure),
            hasLupus: i(values.hasLupus),
            historyParalysis: i(values.historyParalysis),
            hasArthritis: i(values.hasArthritis),
            historyNeuropathy: i(values.historyNeuropathy),
            historyDepression: i(values.historyDepression),
            historyAnxiety: i(values.historyAnxiety),
            hasHistoryOfPsychiatric: i(values.hasHistoryOfPsychiatric),
            psychiatricDescription: values.psychiatricDescription,
            historyFibroidUterus: i(values.historyFibroidUterus),
            historyEndometriosis: i(values.historyEndometriosis),
            historyPolycysticOvaries: i(values.historyPolycysticOvaries),
            hasHadMiscarriages: i(values.hasHadMiscarriages),
            howManyMiscarriages: i(values.howManyMiscarriages),
            hadLateMiscarriage: toBoolOrNull(values.hadLateMiscarriage),
            hasHadAbortions: i(values.hasHadAbortions),
            howManyAbortions: i(values.howManyAbortions),
            hasReceivedTransfusion: i(values.hasReceivedTransfusion),
            howManyTransfusions: i(values.howManyTransfusions),
            hasHistoryOfObesity: i(values.hasHistoryOfObesity),
            hasFabrys: i(values.hasFabrys),
            hasSickleCell: i(values.hasSickleCell),
            hasSickleCellTrait: i(values.hasSickleCellTrait),
            hasSarcoidosis: i(values.hasSarcoidosis),
            hasAutoImmune: i(values.hasAutoImmune),
            autoImmuneDescription: values.autoImmuneDescription,
            hasHadPhysicalExam: i(values.hasHadPhysicalExam),
            physicalExamYearsAgo: i(values.physicalExamYearsAgo),
            hasHadMammogram: i(values.hasHadMammogram),
            mammogramYearsAgo: i(values.mammogramYearsAgo),
            hasHadPapSmear: i(values.hasHadPapSmear),
            papSmearYearsAgo: i(values.papSmearYearsAgo),
            hasHadPsaTest: i(values.hasHadPsaTest),
            psaTestYearsAgo: i(values.psaTestYearsAgo),
            hasHadColonoscopy: i(values.hasHadColonoscopy),
            colonoscopyYearsAgo: i(values.colonoscopyYearsAgo),
            hasAnimalBiteExposure: i(values.hasAnimalBiteExposure),
            animalBiteDescription: values.animalBiteDescription,
            hasActiveInfection: i(values.hasActiveInfection),
            activeInfectionDescription: values.activeInfectionDescription,
            additionalComments: values.additionalComments,
        }),
        (nextStep) => ({ nextStep }),
    );

    return { model, isLoading, error, submit, isSubmitting, submitError };
};
